import { Static, TObject, Type } from '@sinclair/typebox';
import { AssetClass } from '../atomic/types';
import { Goal } from '../goals';
import { FinancialEntityFieldsFragment } from '../graphql/generated';
import { app } from '../zapatos';

export type FinancialEntityTypename = app.financial_entity_type;

export type FinancialEntityInfo =
    | BrokerageAccountInfo
    | CarLoanInfo
    | CarInfo
    | CashHoldingInfo
    | CDInfo
    | CheckingAccountInfo
    | CreditCardInfo
    | HomeMortgageInfo
    | HSAInfo
    | LineOfCreditInfo
    | MoneyMarketAccountInfo
    | OtherAssetInfo
    | OtherInvestmentInfo
    | OtherLiabilityInfo
    | PlentyGoalAccountInfo
    | RealEstatePropertyInfo
    | RetirementAccountInfo
    | S529AccountInfo
    | SavingsAccountInfo
    | StockPlanInfo
    | StudentLoanInfo;

export type FinancialEntitySnapshotInfo =
    | BrokerageAccountSnapshotInfo
    | CarLoanSnapshotInfo
    | CarSnapshotInfo
    | CashHoldingSnapshotInfo
    | CDSnapshotInfo
    | CheckingAccountSnapshotInfo
    | CreditCardSnapshotInfo
    | HomeMortgageSnapshotInfo
    | HSASnapshotInfo
    | LineOfCreditSnapshotInfo
    | MoneyMarketAccountSnapshotInfo
    | OtherAssetSnapshotInfo
    | OtherInvestmentSnapshotInfo
    | OtherLiabilitySnapshotInfo
    | PlentyGoalAccountSnapshotInfo
    | RealEstatePropertySnapshotInfo
    | RetirementAccountSnapshotInfo
    | S529AccountSnapshotInfo
    | SavingsAccountSnapshotInfo
    | StockPlanSnapshotInfo
    | StudentLoanSnapshotInfo;

const RecurrenceWeeklySchema = Type.Object({
    freq: Type.Literal('WEEKLY'),
    interval: Type.Literal(1),
});

const RecurrenceBiweeklySchema = Type.Object({
    freq: Type.Literal('WEEKLY'),
    interval: Type.Literal(2),
});

const RecurrenceMonthlySchema = Type.Object({
    freq: Type.Literal('MONTHLY'),
    interval: Type.Literal(1),
});

const RecurrenceQuarterlySchema = Type.Object({
    freq: Type.Literal('MONTHLY'),
    interval: Type.Literal(3),
});

const RecurrenceYearlySchema = Type.Object({
    freq: Type.Literal('YEARLY'),
    interval: Type.Literal(1),
});

const RecurrenceSchema = Type.Union([
    RecurrenceWeeklySchema,
    RecurrenceBiweeklySchema,
    RecurrenceMonthlySchema,
    RecurrenceQuarterlySchema,
    RecurrenceYearlySchema,
]);

const AmountRecurrenceSchema = Type.Object({
    amount: Type.Number(),
    recurrence: RecurrenceSchema,
});
export type AmountRecurrence = Static<typeof AmountRecurrenceSchema>;

export const BrokerageAccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('BrokerageAccount'),
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
        expectedContribution: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const BrokerageAccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('BrokerageAccount'),
        investedAmount: Type.Optional(Type.Integer()), // integer cents
        returnValue: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export type BrokerageAccountInfo = Static<typeof BrokerageAccountInfoSchema>;
export type BrokerageAccountSnapshotInfo = Static<
    typeof BrokerageAccountSnapshotInfoSchema
>;

export const CarLoanInfoSchema = Type.Object(
    {
        feType: Type.Literal('CarLoan'),
        originalLoanAmount: Type.Optional(Type.Integer()), // integer cents
        loanStartDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        maturityDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        currentPayment: Type.Optional(Type.Integer()), // integer cents
        minimumPayment: Type.Optional(Type.Integer()), // integer cents
        plannedPayment: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const CarLoanSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('CarLoan'),
    },
    { additionalProperties: false }
);
export type CarLoanInfo = Static<typeof CarLoanInfoSchema>;
export type CarLoanSnapshotInfo = Static<typeof CarLoanSnapshotInfoSchema>;

export const CarInfoSchema = Type.Object(
    {
        feType: Type.Literal('Car'),
        make: Type.Optional(Type.String()), // floating-point rate
        model: Type.Optional(Type.String()),
        year: Type.Optional(Type.Number()),
        purchaseAmount: Type.Optional(Type.Number()), // integer cents
        expectedReturn: Type.Optional(Type.Number()), // floating-point-rate
    },
    { additionalProperties: false }
);
export const CarSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('Car'),
    },
    { additionalProperties: false }
);
export type CarInfo = Static<typeof CarInfoSchema>;
export type CarSnapshotInfo = Static<typeof CarSnapshotInfoSchema>;

export const CashHoldingInfoSchema = Type.Object(
    {
        feType: Type.Literal('CashHolding'),
    },
    { additionalProperties: false }
);
export const CashHoldingSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('CashHolding'),
    },
    { additionalProperties: false }
);
export type CashHoldingInfo = Static<typeof CashHoldingInfoSchema>;
export type CashHoldingSnapshotInfo = Static<
    typeof CashHoldingSnapshotInfoSchema
>;

export const CDInfoSchema = Type.Object(
    {
        feType: Type.Literal('CD'),
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        expectedChange: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const CDSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('CD'),
    },
    { additionalProperties: false }
);
export type CDInfo = Static<typeof CDInfoSchema>;
export type CDSnapshotInfo = Static<typeof CDSnapshotInfoSchema>;

export const CheckingAccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('CheckingAccount'),
        interestRate: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export const CheckingAccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('CheckingAccount'),
    },
    { additionalProperties: false }
);
export type CheckingAccountInfo = Static<typeof CheckingAccountInfoSchema>;
export type CheckingAccountSnapshotInfo = Static<
    typeof CheckingAccountSnapshotInfoSchema
>;

const CreditCardInterestRateTypeSchema = Type.Union([
    Type.Literal('BALANCE_TRANSFER'),
    Type.Literal('CASH'),
    Type.Literal('OTHER'),
    Type.Literal('PURCHASE'),
]);
export const CreditCardInfoSchema = Type.Object(
    {
        feType: Type.Literal('CreditCard'),
        isPaidOffMonthly: Type.Optional(Type.Boolean()),
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        interestRateType: Type.Optional(CreditCardInterestRateTypeSchema),
        currentPayment: Type.Optional(Type.Integer()), // integer cents
        minimumPayment: Type.Optional(Type.Integer()), // integer cents
        plannedPayment: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const CreditCardSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('CreditCard'),
    },
    { additionalProperties: false }
);
export type CreditCardInterestRateType = Static<
    typeof CreditCardInterestRateTypeSchema
>;
export type CreditCardInfo = Static<typeof CreditCardInfoSchema>;
export type CreditCardSnapshotInfo = Static<
    typeof CreditCardSnapshotInfoSchema
>;

export const HomeMortgageInfoSchema = Type.Object(
    {
        feType: Type.Literal('HomeMortgage'),
        originalLoanAmount: Type.Optional(Type.Integer()), // integer cents
        loanStartDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        maturityDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        currentPayment: Type.Optional(Type.Integer()), // integer cents
        minimumPayment: Type.Optional(Type.Integer()), // integer cents
        plannedPayment: Type.Optional(AmountRecurrenceSchema),
        loanType: Type.Optional(Type.String()),
        loanTermYears: Type.Optional(Type.Number()),
        hasPrepaymentPenalty: Type.Optional(Type.Boolean()),
        hasPMI: Type.Optional(Type.Boolean()),
    },
    { additionalProperties: false }
);
export const HomeMortgageSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('HomeMortgage'),
    },
    { additionalProperties: false }
);
export type HomeMortgageInfo = Static<typeof HomeMortgageInfoSchema>;
export type HomeMortgageSnapshotInfo = Static<
    typeof HomeMortgageSnapshotInfoSchema
>;

export const HSAInfoSchema = Type.Object(
    {
        feType: Type.Literal('HSA'),
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
        expectedContribution: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const HSASnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('HSA'),
        investedAmount: Type.Optional(Type.Integer()), // integer cents
        returnValue: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export type HSAInfo = Static<typeof HSAInfoSchema>;
export type HSASnapshotInfo = Static<typeof HSASnapshotInfoSchema>;

export const LineOfCreditInfoSchema = Type.Object(
    {
        feType: Type.Literal('LineOfCredit'),
        originalLoanAmount: Type.Optional(Type.Integer()), // integer cents
        loanStartDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        maturityDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        currentPayment: Type.Optional(Type.Integer()), // integer cents
        minimumPayment: Type.Optional(Type.Integer()), // integer cents
        plannedPayment: Type.Optional(AmountRecurrenceSchema),
        earlyRepaymentPenalty: Type.Optional(Type.Integer()), // integer cents
    },
    { additionalProperties: false }
);
export const LineOfCreditSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('LineOfCredit'),
    },
    { additionalProperties: false }
);
export type LineOfCreditInfo = Static<typeof LineOfCreditInfoSchema>;
export type LineOfCreditSnapshotInfo = Static<
    typeof LineOfCreditSnapshotInfoSchema
>;

export const MoneyMarketAccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('MoneyMarketAccount'),
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        expectedChange: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const MoneyMarketAccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('MoneyMarketAccount'),
    },
    { additionalProperties: false }
);
export type MoneyMarketAccountInfo = Static<
    typeof MoneyMarketAccountInfoSchema
>;
export type MoneyMarketAccountSnapshotInfo = Static<
    typeof MoneyMarketAccountSnapshotInfoSchema
>;

export const OtherAssetSubtypeSchema = Type.Union([
    Type.Literal('ART'),
    Type.Literal('JEWELRY'),
    Type.Literal('COLLECTIBLES'),
    Type.Literal('EBT'), // Electronic benefit transfer
    Type.Literal('GIC'), // Guaranteed investment certificate
    Type.Literal('UGMA'), // Uniform Gift to Minors Act
    Type.Literal('UTMA'), // Uniform Transfer to Minors Act
    Type.Literal('INSURANCE'),
    Type.Literal('LIFE_INSURANCE'),
    Type.Literal('PROFIT_SHARING_PLAN'),
    Type.Literal('OTHER'),
]);
export const OtherAssetInfoSchema = Type.Object(
    {
        feType: Type.Literal('OtherAsset'),
        subtype: Type.Optional(OtherAssetSubtypeSchema),
        purchaseAmount: Type.Optional(Type.Integer()), // integer cents
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export const OtherAssetSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('OtherAsset'),
    },
    { additionalProperties: false }
);
export type OtherAssetInfo = Static<typeof OtherAssetInfoSchema>;
export type OtherAssetSnapshotInfo = Static<
    typeof OtherAssetSnapshotInfoSchema
>;
export type OtherAssetSubtype = Static<typeof OtherAssetSubtypeSchema>;

export const OtherInvestmentSubtypeSchema = Type.Union([
    Type.Literal('CRYPTO'),
    Type.Literal('ESPP'), // Employee Stock Purchase Plan
    Type.Literal('ESA'), // Education Savings Account
    Type.Literal('RSU'), // Restricted Stock Units
    Type.Literal('TAX_DEFERRED'),
    Type.Literal('ANNUITY'),
    Type.Literal('TRUST'),
    Type.Literal('OTHER'),
]);

export const OtherInvestmentInfoSchema = Type.Object(
    {
        feType: Type.Literal('OtherInvestment'),
        subtype: Type.Optional(OtherInvestmentSubtypeSchema),
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
        expectedContribution: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const OtherInvestmentSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('OtherInvestment'),
        investedAmount: Type.Optional(Type.Integer()), // integer cents
        returnValue: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export type OtherInvestmentInfo = Static<typeof OtherInvestmentInfoSchema>;
export type OtherInvestmentSnapshotInfo = Static<
    typeof OtherInvestmentSnapshotInfoSchema
>;
export type OtherInvestmentSubtype = Static<
    typeof OtherInvestmentSubtypeSchema
>;

export const OtherLiabilityInfoSchema = Type.Object(
    {
        feType: Type.Literal('OtherLiability'),
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        currentPayment: Type.Optional(Type.Integer()), // integer cents
        minimumPayment: Type.Optional(Type.Integer()), // integer cents
        plannedPayment: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const OtherLiabilitySnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('OtherLiability'),
    },
    { additionalProperties: false }
);
export type OtherLiabilityInfo = Static<typeof OtherLiabilityInfoSchema>;
export type OtherLiabilitySnapshotInfo = Static<
    typeof OtherLiabilitySnapshotInfoSchema
>;

export const PlentyGoalAccountTypeSchema = Type.Union([
    Type.Literal('BROKERAGE'),
    Type.Literal('ROTH_IRA'),
    Type.Literal('TRADITIONAL_IRA'),
    Type.Literal('JOINT_BROKERAGE'),
]);
export const PlentyGoalAccountAssetClassExposuresSchema = Type.Array(
    Type.Object({
        name: AssetClass,
        fraction: Type.Number(),
    })
);
export const PlentyGoalAccountInvestmentTypeSchema = Type.Union([
    Type.Literal('HIGH_YIELD_CASH'),
    Type.Literal('DIRECT_INDEXING'),
    Type.Literal('CLASSIC_PORTFOLIO'),
]);
export const PlentyGoalAccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('PlentyGoalAccount'),
        accountType: PlentyGoalAccountTypeSchema,
        assetClassExposures: Type.Optional(
            PlentyGoalAccountAssetClassExposuresSchema
        ),
        investmentType: PlentyGoalAccountInvestmentTypeSchema,
        riskScore: Type.Integer(),
    },
    { additionalProperties: false }
);
export const PlentyGoalAccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('PlentyGoalAccount'),
        costBasis: Type.Optional(Type.Integer()), // integer cents
        changeTodayAmount: Type.Optional(Type.Integer()), // integer cents
        changeTodayRate: Type.Optional(Type.Number()), // floating-point rate
        simpleReturn: Type.Optional(Type.Number()), // floating-point rate
        timeWeightedReturn: Type.Optional(Type.Number()), // floating-point rate
        totalInterestAmount: Type.Optional(Type.Integer()), // integer cents
        totalDividendAmount: Type.Optional(Type.Integer()), // integer cents
        totalFeesAmount: Type.Optional(Type.Integer()), // integer cents
        realizedPNLAmount: Type.Optional(Type.Integer()), // integer cents
        unrealizedPNLAmount: Type.Optional(Type.Integer()), // integer cents
        withdrawalsAmount: Type.Optional(Type.Integer()), // integer cents
        contributionsAmount: Type.Optional(Type.Integer()), // integer cents
        availableToWithdrawAmount: Type.Optional(Type.Integer()), // integer cents
    },
    { additionalProperties: false }
);
export type PlentyGoalAccountType = Static<typeof PlentyGoalAccountTypeSchema>;
export type PlentyGoalAccountInfo = Static<typeof PlentyGoalAccountInfoSchema>;
export type PlentyGoalAccountSnapshotInfo = Static<
    typeof PlentyGoalAccountSnapshotInfoSchema
>;

export const RealEstatePropertySubtypeSchema = Type.Union([
    Type.Literal('PRIMARY'),
    Type.Literal('VACATION'),
    Type.Literal('RENTAL'),
]);
export const RealEstatePropertyInfoSchema = Type.Object(
    {
        feType: Type.Literal('RealEstateProperty'),
        subtype: Type.Optional(RealEstatePropertySubtypeSchema),
        purchaseAmount: Type.Optional(Type.Integer()), // integer cents
        purchaseDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export const RealEstatePropertySnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('RealEstateProperty'),
    },
    { additionalProperties: false }
);
export type RealEstatePropertySubtype = Static<
    typeof RealEstatePropertySubtypeSchema
>;
export type RealEstatePropertyInfo = Static<
    typeof RealEstatePropertyInfoSchema
>;
export type RealEstatePropertySnapshotInfo = Static<
    typeof RealEstatePropertySnapshotInfoSchema
>;

export const RetirementAccountSubtypeSchema = Type.Union([
    Type.Literal('IRA'),
    Type.Literal('KEOGH'),
    Type.Literal('PENSION'),
    Type.Literal('ROTH_401K'),
    Type.Literal('ROTH_IRA'),
    Type.Literal('SEP_IRA'),
    Type.Literal('THRIFT_SAVINGS_PLAN'),
    Type.Literal('_401A'),
    Type.Literal('_401K'),
    Type.Literal('_403B'),
    Type.Literal('_457B'),
]);
export const RetirementAccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('RetirementAccount'),
        subtype: RetirementAccountSubtypeSchema,
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
        expectedContribution: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const RetirementAccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('RetirementAccount'),
        investedAmount: Type.Optional(Type.Integer()), // integer cents
        returnValue: Type.Optional(Type.Number()), // floating-point rate
        contributedAmount: Type.Optional(Type.Integer()), // integer cents
        contributedPercentOfLimit: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export type RetirementAccountSubtype = Static<
    typeof RetirementAccountSubtypeSchema
>;
export type RetirementAccountInfo = Static<typeof RetirementAccountInfoSchema>;
export type RetirementAccountSnapshotInfo = Static<
    typeof RetirementAccountSnapshotInfoSchema
>;

export const S529AccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('S529Account'),
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
        expectedContribution: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const S529AccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('S529Account'),
        investedAmount: Type.Optional(Type.Integer()), // integer cents
        returnValue: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export type S529AccountInfo = Static<typeof S529AccountInfoSchema>;
export type S529AccountSnapshotInfo = Static<
    typeof S529AccountSnapshotInfoSchema
>;

export const SavingsAccountInfoSchema = Type.Object(
    {
        feType: Type.Literal('SavingsAccount'),
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        expectedChange: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const SavingsAccountSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('SavingsAccount'),
    },
    { additionalProperties: false }
);
export type SavingsAccountInfo = Static<typeof SavingsAccountInfoSchema>;
export type SavingsAccountSnapshotInfo = Static<
    typeof SavingsAccountSnapshotInfoSchema
>;

export const StockPlanInfoSchema = Type.Object(
    {
        feType: Type.Literal('StockPlan'),
        expectedReturn: Type.Optional(Type.Number()), // floating-point rate
        expectedContribution: Type.Optional(AmountRecurrenceSchema),
    },
    { additionalProperties: false }
);
export const StockPlanSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('StockPlan'),
        investedAmount: Type.Optional(Type.Integer()), // integer cents
        returnValue: Type.Optional(Type.Number()), // floating-point rate
    },
    { additionalProperties: false }
);
export type StockPlanInfo = Static<typeof StockPlanInfoSchema>;
export type StockPlanSnapshotInfo = Static<typeof StockPlanSnapshotInfoSchema>;

export const StudentLoanInfoSchema = Type.Object(
    {
        feType: Type.Literal('StudentLoan'),
        originalLoanAmount: Type.Optional(Type.Integer()), // integer cents
        loanStartDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        maturityDate: Type.Optional(Type.String({ format: 'date' })), // YYYY-MM-DD
        interestRate: Type.Optional(Type.Number()), // floating-point rate
        currentPayment: Type.Optional(Type.Integer()), // integer cents
        minimumPayment: Type.Optional(Type.Integer()), // integer cents
        plannedPayment: Type.Optional(AmountRecurrenceSchema),
        earlyRepaymentPenalty: Type.Optional(Type.Integer()), // integer cents
    },
    { additionalProperties: false }
);
export const StudentLoanSnapshotInfoSchema = Type.Object(
    {
        feType: Type.Literal('StudentLoan'),
    },
    { additionalProperties: false }
);
export type StudentLoanInfo = Static<typeof StudentLoanInfoSchema>;
export type StudentLoanSnapshotInfo = Static<
    typeof StudentLoanSnapshotInfoSchema
>;

export const feInfoSchemaByFEType = {
    BrokerageAccount: BrokerageAccountInfoSchema,
    CarLoan: CarLoanInfoSchema,
    Car: CarInfoSchema,
    CashHolding: CashHoldingInfoSchema,
    CD: CDInfoSchema,
    CheckingAccount: CheckingAccountInfoSchema,
    CreditCard: CreditCardInfoSchema,
    HomeMortgage: HomeMortgageInfoSchema,
    HSA: HSAInfoSchema,
    LineOfCredit: LineOfCreditInfoSchema,
    MoneyMarketAccount: MoneyMarketAccountInfoSchema,
    OtherAsset: OtherAssetInfoSchema,
    OtherInvestment: OtherInvestmentInfoSchema,
    OtherLiability: OtherLiabilityInfoSchema,
    PlentyGoalAccount: PlentyGoalAccountInfoSchema,
    RealEstateProperty: RealEstatePropertyInfoSchema,
    RetirementAccount: RetirementAccountInfoSchema,
    S529Account: S529AccountInfoSchema,
    SavingsAccount: SavingsAccountInfoSchema,
    StockPlan: StockPlanInfoSchema,
    StudentLoan: StudentLoanInfoSchema,
} satisfies Record<FinancialEntityTypename, TObject>;

export const feSnapshotInfoSchemaByFEType = {
    BrokerageAccount: BrokerageAccountSnapshotInfoSchema,
    CarLoan: CarLoanSnapshotInfoSchema,
    Car: CarSnapshotInfoSchema,
    CashHolding: CashHoldingSnapshotInfoSchema,
    CD: CDSnapshotInfoSchema,
    CheckingAccount: CheckingAccountSnapshotInfoSchema,
    CreditCard: CreditCardSnapshotInfoSchema,
    HomeMortgage: HomeMortgageSnapshotInfoSchema,
    HSA: HSASnapshotInfoSchema,
    LineOfCredit: LineOfCreditSnapshotInfoSchema,
    MoneyMarketAccount: MoneyMarketAccountSnapshotInfoSchema,
    OtherAsset: OtherAssetSnapshotInfoSchema,
    OtherInvestment: OtherInvestmentSnapshotInfoSchema,
    OtherLiability: OtherLiabilitySnapshotInfoSchema,
    PlentyGoalAccount: PlentyGoalAccountSnapshotInfoSchema,
    RealEstateProperty: RealEstatePropertySnapshotInfoSchema,
    RetirementAccount: RetirementAccountSnapshotInfoSchema,
    S529Account: S529AccountSnapshotInfoSchema,
    SavingsAccount: SavingsAccountSnapshotInfoSchema,
    StockPlan: StockPlanSnapshotInfoSchema,
    StudentLoan: StudentLoanSnapshotInfoSchema,
} satisfies Record<FinancialEntityTypename, TObject>;

export type FEInfoByFEType<T extends FinancialEntityTypename> = {
    BrokerageAccount: BrokerageAccountInfo;
    CarLoan: CarLoanInfo;
    Car: CarInfo;
    CashHolding: CashHoldingInfo;
    CD: CDInfo;
    CheckingAccount: CheckingAccountInfo;
    CreditCard: CreditCardInfo;
    HomeMortgage: HomeMortgageInfo;
    HSA: HSAInfo;
    LineOfCredit: LineOfCreditInfo;
    MoneyMarketAccount: MoneyMarketAccountInfo;
    OtherAsset: OtherAssetInfo;
    OtherInvestment: OtherInvestmentInfo;
    OtherLiability: OtherLiabilityInfo;
    PlentyGoalAccount: PlentyGoalAccountInfo;
    RealEstateProperty: RealEstatePropertyInfo;
    RetirementAccount: RetirementAccountInfo;
    S529Account: S529AccountInfo;
    SavingsAccount: SavingsAccountInfo;
    StockPlan: StockPlanInfo;
    StudentLoan: StudentLoanInfo;
}[T];

export type FESnapshotInfoByFEType<T extends FinancialEntityTypename> = {
    BrokerageAccount: BrokerageAccountSnapshotInfo;
    CarLoan: CarLoanSnapshotInfo;
    Car: CarSnapshotInfo;
    CashHolding: CashHoldingSnapshotInfo;
    CD: CDSnapshotInfo;
    CheckingAccount: CheckingAccountSnapshotInfo;
    CreditCard: CreditCardSnapshotInfo;
    HomeMortgage: HomeMortgageSnapshotInfo;
    HSA: HSASnapshotInfo;
    LineOfCredit: LineOfCreditSnapshotInfo;
    OtherAsset: OtherAssetSnapshotInfo;
    OtherInvestment: OtherInvestmentSnapshotInfo;
    OtherLiability: OtherLiabilitySnapshotInfo;
    MoneyMarketAccount: MoneyMarketAccountSnapshotInfo;
    PlentyGoalAccount: PlentyGoalAccountSnapshotInfo;
    RealEstateProperty: RealEstatePropertySnapshotInfo;
    RetirementAccount: RetirementAccountSnapshotInfo;
    S529Account: S529AccountSnapshotInfo;
    SavingsAccount: SavingsAccountSnapshotInfo;
    StockPlan: StockPlanSnapshotInfo;
    StudentLoan: StudentLoanSnapshotInfo;
}[T];

export type FinancialType = 'asset' | 'debt';
export type FinancialCategory =
    | 'CashAccount'
    | 'Investment'
    | 'Debt'
    | 'CreditCard'
    | 'Retirement'
    | 'OtherAsset';

// BrokerageAccount
export interface BrokerageAccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'BrokerageAccount';
    info: BrokerageAccountInfo;
}

// CarLoan
export interface CarLoanFinancialEntity extends FinancialEntityFieldsFragment {
    type: 'CarLoan';
    info: CarLoanInfo;
}

// Car
export interface CarFinancialEntity extends FinancialEntityFieldsFragment {
    type: 'Car';
    info: CarInfo;
}

// CashHolding
export interface CashHoldingFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'CashHolding';
    info: CashHoldingInfo;
}

// CD
export interface CDFinancialEntity extends FinancialEntityFieldsFragment {
    type: 'CD';
    info: CDInfo;
}

// CheckingAccount
export interface CheckingAccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'CheckingAccount';
    info: CheckingAccountInfo;
}

// CreditCard
export interface CreditCardFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'CreditCard';
    info: CreditCardInfo;
}

// HomeMortgage
export interface HomeMortgageFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'HomeMortgage';
    info: HomeMortgageInfo;
}

// HSA
export interface HSAFinancialEntity extends FinancialEntityFieldsFragment {
    type: 'HSA';
    info: HSAInfo;
}

// LineOfCredit
export interface LineOfCreditFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'LineOfCredit';
    info: LineOfCreditInfo;
}

// MoneyMarketAccount
export interface MoneyMarketAccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'MoneyMarketAccount';
    info: MoneyMarketAccountInfo;
}

// OtherAsset
export interface OtherAssetFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'OtherAsset';
    info: OtherAssetInfo;
}

// OtherInvestment
export interface OtherInvestmentFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'OtherInvestment';
    info: OtherInvestmentInfo;
}

// OtherLiability
export interface OtherLiabilityFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'OtherLiability';
    info: OtherLiabilityInfo;
}

// PlentyGoalAccount
export interface PlentyGoalAccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'PlentyGoalAccount';
    info: PlentyGoalAccountInfo;
}

// RealEstateProperty
export interface RealEstatePropertyFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'RealEstateProperty';
    info: RealEstatePropertyInfo;
}

// RetirementAccount
export interface RetirementAccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'RetirementAccount';
    info: RetirementAccountInfo;
}

// S529Account
export interface S529AccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'S529Account';
    info: S529AccountInfo;
}

// SavingsAccount
export interface SavingsAccountFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'SavingsAccount';
    info: SavingsAccountInfo;
}

// StockPlan
export interface StockPlanFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'StockPlan';
    info: StockPlanInfo;
}

// StudentLoan
export interface StudentLoanFinancialEntity
    extends FinancialEntityFieldsFragment {
    type: 'StudentLoan';
    info: StudentLoanInfo;
}

export type AnyAsset =
    | CashHoldingFinancialEntity
    | CheckingAccountFinancialEntity
    | SavingsAccountFinancialEntity
    | BrokerageAccountFinancialEntity
    | RetirementAccountFinancialEntity
    | S529AccountFinancialEntity
    | RealEstatePropertyFinancialEntity
    | CarFinancialEntity
    | OtherAssetFinancialEntity
    | OtherInvestmentFinancialEntity
    | CDFinancialEntity
    | MoneyMarketAccountFinancialEntity
    | HSAFinancialEntity
    | StockPlanFinancialEntity;

export type AnyDepositoryAccount =
    | CheckingAccountFinancialEntity
    | SavingsAccountFinancialEntity;

export type AnyDebt =
    | CreditCardFinancialEntity
    | HomeMortgageFinancialEntity
    | CarLoanFinancialEntity
    | StudentLoanFinancialEntity
    | LineOfCreditFinancialEntity
    | OtherLiabilityFinancialEntity;

export type ExternalFinancialEntity = AnyAsset | AnyDebt;

export type ExternalFinancialEntityType = ExternalFinancialEntity['type'];

export type AnyFinancialEntity =
    | ExternalFinancialEntity
    | PlentyGoalAccountFinancialEntity;

export type ExternalFinancialEntitiesMap<
    T,
    K extends string | number | symbol = ExternalFinancialEntityType,
> = Record<K, T>;

export type FEGetter<R> = (feType: ExternalFinancialEntityType) => R;

export const ASSET_TYPES: ExternalFinancialEntityType[] = [
    'CashHolding',
    'CheckingAccount',
    'SavingsAccount',
    'CD',
    'MoneyMarketAccount',
    'BrokerageAccount',
    'RetirementAccount',
    'S529Account',
    'RealEstateProperty',
    'Car',
    'OtherAsset',
    'OtherInvestment',
    'HSA',
    'StockPlan',
];
export const DEPOSITORY_ACCOUNT_TYPES: ExternalFinancialEntityType[] = [
    'CheckingAccount',
    'SavingsAccount',
];
export const DEBT_TYPES: ExternalFinancialEntityType[] = [
    'CreditCard',
    'HomeMortgage',
    'StudentLoan',
    'CarLoan',
    'LineOfCredit',
    'OtherLiability',
];

// Convenience for looking up goal from goal account in account list item
export type GoalAccountWithGoal = PlentyGoalAccountFinancialEntity & {
    goal: Goal;
};

export const isAsset = (fe: ExternalFinancialEntity): boolean =>
    ASSET_TYPES.includes(fe.type);
export const isDepositoryAccount = (
    fe: ExternalFinancialEntity
): fe is AnyDepositoryAccount => DEPOSITORY_ACCOUNT_TYPES.includes(fe.type);
export const isDebt = (fe: ExternalFinancialEntity): boolean =>
    DEBT_TYPES.includes(fe.type);

export const isPlentyGoalAccount = (
    fe: FinancialEntityFieldsFragment
): fe is PlentyGoalAccountFinancialEntity => {
    return fe.type === 'PlentyGoalAccount';
};
export const isExternalFinancialEntity = (
    fe: FinancialEntityFieldsFragment
): fe is ExternalFinancialEntity => {
    return fe.type !== 'PlentyGoalAccount';
};
